(() => {
  if (customElements.get('alleycat-hero')) return;
  const heroes = new Set();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  let frame = 0;
  let deferredLayoutFrame = 0;
  let placementPending = false;
  let managedHeader = null;
  let headerHero = null;
  let listenerController = null;
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const setStyle = (element, name, value) => {
    if (element.style.getPropertyValue(name) !== value) element.style.setProperty(name, value);
  };
  const visibleSections = (parent) => [...(parent?.children || [])].filter(
    (el) => el.classList.contains('shopify-section') && el.getClientRects().length
  );

  const pixels = (style, name) => parseFloat(style.getPropertyValue(name)) || 0;
  const boundedOffset = (requested, min, max) => min <= max ? clamp(requested, min, max) : 0;

  // Die Regler verschieben den Inhalt wirklich, aber nur innerhalb der
  // gepolsterten Hero-Fläche. Die Messung entfernt die letzte Verschiebung,
  // damit Resize, Schriftwechsel und Editor-Neuladen keine Offsets addieren.
  const placeContent = () => {
    const placements = [];
    heroes.forEach((hero) => {
      const content = hero.querySelector('.alleycat-hero__content');
      const stack = hero.querySelector('.alleycat-hero__stack');
      if (!content || !stack || !content.getClientRects().length) return;
      const heroStyle = getComputedStyle(hero);
      const contentStyle = getComputedStyle(content);
      const contentRect = content.getBoundingClientRect();
      const stackRect = stack.getBoundingClientRect();
      const previousX = pixels(stack.style, '--ac-stack-shift-x');
      const previousY = pixels(stack.style, '--ac-stack-shift-y');
      const left = contentRect.left + pixels(contentStyle, 'padding-left');
      const right = contentRect.right - pixels(contentStyle, 'padding-right');
      const top = contentRect.top + pixels(contentStyle, 'padding-top');
      const bottom = contentRect.bottom - pixels(contentStyle, 'padding-bottom');
      const x = boundedOffset(pixels(heroStyle, '--ac-block-x'),
        left - (stackRect.left - previousX), right - (stackRect.right - previousX));
      const y = boundedOffset(pixels(heroStyle, '--ac-block-y'),
        top - (stackRect.top - previousY), bottom - (stackRect.bottom - previousY));
      const items = [...stack.querySelectorAll('.alleycat-hero__item')].map((item) => {
        const itemRect = item.getBoundingClientRect();
        const previousItemX = pixels(item.style, '--ac-item-shift-x');
        const baseLeft = itemRect.left - previousX - previousItemX + x;
        const baseRight = itemRect.right - previousX - previousItemX + x;
        return { item, x: boundedOffset(pixels(getComputedStyle(item), '--ac-item-x'),
          left - baseLeft, right - baseRight) };
      });
      placements.push({ stack, x, y, items });
    });
    // Erst alle Rechtecke lesen, danach schreiben. Transforms ändern keine
    // ResizeObserver-Box; identische Werte werden nicht erneut geschrieben.
    placements.forEach(({ stack, x, y, items }) => {
      setStyle(stack, '--ac-stack-shift-x', x + 'px');
      setStyle(stack, '--ac-stack-shift-y', y + 'px');
      items.forEach(({ item, x: itemX }) => setStyle(item, '--ac-item-shift-x', itemX + 'px'));
    });
  };

  // Nur die Bildebene wird nach oben verlängert. Header, Laufschrift und
  // Abschnitt selbst behalten ihren Platz im Seitenlayout.
  const syncHeader = () => {
    const header = document.querySelector('.section-header');
    if (managedHeader && managedHeader !== header) managedHeader.classList.remove('ac-hero-header');
    managedHeader = header;
    headerHero = null;
    heroes.forEach((hero) => {
      let overlap = 0;
      const section = hero.closest('.shopify-section');
      const main = document.getElementById('MainContent');
      if (header && !headerHero) {
        const groupSections = visibleSections(header.parentElement);
        const inHeaderGroup = groupSections[groupSections.indexOf(header) + 1] === section;
        const firstInMain = section?.parentElement === main && visibleSections(main)[0] === section;
        const lastHeaderSection = [...document.querySelectorAll('.shopify-section-group-header-group')]
          .filter((el) => el.getClientRects().length).at(-1);
        if (inHeaderGroup || (firstInMain && (!lastHeaderSection || lastHeaderSection === header))) {
          // offsetHeight ist unabhängig vom Ein-/Ausblenden des Sticky-Headers.
          overlap = header.offsetHeight + (parseFloat(getComputedStyle(header).marginBottom) || 0);
          headerHero = hero;
        }
      }
      setStyle(hero, '--ac-overlap', overlap + 'px');
    });
    if (!headerHero) managedHeader?.classList.remove('ac-hero-header');
  };

  const update = () => {
    frame = 0;
    if (placementPending) {
      placementPending = false;
      placeContent();
    }
    if (managedHeader && headerHero) {
      const overImage = headerHero.getBoundingClientRect().bottom > managedHeader.getBoundingClientRect().bottom + 16;
      managedHeader.classList.toggle('ac-hero-header', overImage);
    }
    heroes.forEach((hero) => {
      if (reduced.matches) {
        setStyle(hero, '--ac-image-shift', '0px');
        return;
      }
      const rect = hero.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > innerHeight) return;
      const mobile = innerWidth <= 749;
      const imageStrength = Number(hero.dataset[mobile ? 'imageMobile' : 'imageDesktop']) || 0;
      const imageTravel = Number.parseFloat(getComputedStyle(hero).getPropertyValue('--ac-image-travel')) || 0;
      // Beim Laden bleibt das Motiv unverrückt. Erst das Vorbeiscrollen bewegt
      // es sanft; maximal um die Reserve. Der normale Button-Hover bleibt frei.
      const distance = Math.max(0, -rect.top);
      setStyle(hero, '--ac-image-shift', clamp(distance * imageStrength, 0, imageTravel) + 'px');

    });
  };
  const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
  const layout = () => { syncHeader(); placementPending = true; schedule(); };
  const onSectionUnload = () => {
    if (deferredLayoutFrame) return;
    deferredLayoutFrame = requestAnimationFrame(() => {
      deferredLayoutFrame = 0;
      if (heroes.size) layout();
    });
  };
  const listen = () => {
    if (listenerController) return;
    listenerController = new AbortController();
    const { signal } = listenerController;
    window.addEventListener('scroll', schedule, { passive: true, signal });
    window.addEventListener('resize', layout, { passive: true, signal });
    window.addEventListener('pageshow', layout, { signal });
    reduced.addEventListener('change', schedule, { signal });
    document.addEventListener('shopify:section:load', layout, { signal });
    document.addEventListener('shopify:section:unload', onSectionUnload, { signal });
    document.addEventListener('shopify:section:reorder', layout, { signal });
    document.fonts?.addEventListener('loadingdone', layout, { signal });
    document.fonts?.ready.then(() => { if (!signal.aborted) layout(); });
  };

  class AlleycatHero extends HTMLElement {
    connectedCallback() {
      if (heroes.has(this)) return;
      heroes.add(this);
      listen();
      this.imageController = new AbortController();
      const image = this.querySelector('img.alleycat-hero__image');
      const revealImage = () => {
        this.revealFrame = requestAnimationFrame(() => this.classList.add('is-image-loaded'));
      };
      if (!image || image.complete) {
        revealImage();
      } else {
        image.addEventListener('load', revealImage, { once: true, signal: this.imageController.signal });
        image.addEventListener('error', revealImage, { once: true, signal: this.imageController.signal });
      }
      this.observer = new ResizeObserver(layout);
      const header = document.querySelector('.section-header .header-wrapper');
      if (header) this.observer.observe(header);
      this.observer.observe(this);
      this.querySelectorAll('.alleycat-hero__content, .alleycat-hero__stack, .alleycat-hero__item')
        .forEach((element) => this.observer.observe(element));
      layout();
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.revealFrame);
      this.imageController?.abort();
      this.observer?.disconnect();
      heroes.delete(this);
      layout();
      if (!heroes.size) {
        listenerController?.abort();
        listenerController = null;
        cancelAnimationFrame(frame);
        cancelAnimationFrame(deferredLayoutFrame);
        frame = 0;
        deferredLayoutFrame = 0;
        placementPending = false;
      }
    }
  }
  customElements.define('alleycat-hero', AlleycatHero);
})();